import React, { useEffect, useRef, useState } from "react";

// Animated count-up to `value` when scrolled into view (first time only).
export const AnimatedCount = ({ value, duration = 1600, className = "", testId = "animated-count" }) => {
  const [display, setDisplay] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf;
    const start = performance.now();
    const from = display;
    const to = value;
    const animate = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [started, value, duration]);

  // After first start, when value changes (e.g. after registration), animate from current display.
  useEffect(() => {
    if (!started) return;
    let raf;
    const start = performance.now();
    const from = display;
    const to = value;
    if (from === to) return;
    const animate = (now) => {
      const t = Math.min(1, (now - start) / 1000);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [value]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {display.toLocaleString()}
    </span>
  );
};

export default AnimatedCount;
